import { defineStore } from 'pinia'

const API_BASE_URL = import.meta.env.DEV 
  ? '/api'  // Use Vite proxy in dev (proxies to http://192.168.0.200:8001/api)
  : '/api'  // Keep as-is for prod (adjust if needed)

export const useAuthStore = defineStore('auth', {
  state: () => ({
    access: localStorage.getItem('access_token'),
    refresh: localStorage.getItem('refresh_token'),
    user: JSON.parse(localStorage.getItem('user')) || null,
    company: JSON.parse(localStorage.getItem('company')) || null,
    refreshPromise: null, // To prevent multiple simultaneous refresh attempts
    tenantDomain: localStorage.getItem('tenant_domain') || 'roster.dgsolutions.se',  // NEW: Default tenant domain
    systemCapabilities: JSON.parse(localStorage.getItem('system_capabilities')) || null, // NEW: System access info
  }),
  getters: {
    isAuthenticated: (state) => !!state.access,
    getUser: (state) => state.user,
    getCompany: (state) => state.company,
    getUserRole: (state) => state.user?.role,
    isManager: (state) => state.user?.role === 'manager',
    isWorker: (state) => state.user?.role === 'worker',
    isSuperuser: (state) => state.user?.is_superuser === true,
    getTenantDomain: (state) => state.tenantDomain,  // NEW: Getter for tenant
    getSystemCapabilities: (state) => state.systemCapabilities, // NEW: System capabilities
    canSchedule: (state) => state.systemCapabilities?.canSchedule || false,
    canManageCompanies: (state) => state.systemCapabilities?.canManageCompanies || false,
    canViewInvoicing: (state) => state.systemCapabilities?.canViewInvoicing || false,
    hasSchedulerAccess: (state) => state.systemCapabilities?.availableSystems?.scheduler || false,
    hasAdminAccess: (state) => state.systemCapabilities?.availableSystems?.admin || false,
  },
  actions: {
    setTokens(access, refresh) {
      this.access = access
      this.refresh = refresh
      localStorage.setItem('access_token', access)
      localStorage.setItem('refresh_token', refresh)
    },
    clearTokens() {
      this.access = null
      this.refresh = null
      this.user = null
      this.company = null
      this.refreshPromise = null
      this.systemCapabilities = null
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user')
      localStorage.removeItem('company')
      localStorage.removeItem('system_capabilities')
      // Clear unified auth tokens too
      localStorage.removeItem('scheduler_token')
      localStorage.removeItem('scheduler_refresh_token')
      localStorage.removeItem('admin_token')
      // NEW: Clear tenant on logout
      this.tenantDomain = 'roster.dgsolutions.se'
      localStorage.removeItem('tenant_domain')
    },
    setUser(user) {
      this.user = user
      localStorage.setItem('user', JSON.stringify(user))
      
      // Store company separately for easy access
      if (user && user.company) {
        this.company = user.company
        localStorage.setItem('company', JSON.stringify(user.company))
      }
    },
    // NEW: Action to set tenant domain dynamically
    setTenantDomain(domain) {
      this.tenantDomain = domain
      localStorage.setItem('tenant_domain', domain)
    },
    async refreshAccessToken() {
      // If a refresh is already in progress, wait for it
      if (this.refreshPromise) {
        return this.refreshPromise
      }

      if (!this.refresh) {
        throw new Error('No refresh token available')
      }

      try {
        this.refreshPromise = fetch(`${API_BASE_URL}/token/refresh/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Host': this.tenantDomain,  // NEW: Add Host header for tenant routing
          },
          body: JSON.stringify({ refresh: this.refresh })
        }).then(async (response) => {
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            throw new Error(errorData.detail || 'Refresh token expired')
          }
          return response.json()
        })

        const data = await this.refreshPromise
        this.setTokens(data.access, this.refresh)
        this.refreshPromise = null
        return data.access
      } catch (error) {
        this.refreshPromise = null
        this.clearTokens()
        
        // Redirect to login page
        if (typeof window !== 'undefined') {
          window.location.href = '/'
        }
        
        throw error
      }
    },
    async makeAuthenticatedRequest(url, options = {}) {
      const makeRequest = async (token) => {
        return fetch(url, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Host': this.tenantDomain,  // NEW: Add Host header for tenant routing
            ...options.headers
          }
        })
      }

      // Check if we have a token
      if (!this.access) {
        this.clearTokens()
        if (typeof window !== 'undefined') {
          window.location.href = '/'
        }
        throw new Error('No access token available')
      }

      // First attempt with current token
      let response = await makeRequest(this.access)

      // If unauthorized, try to refresh token and retry ONCE
      if (response.status === 401) {
        try {
          const newToken = await this.refreshAccessToken()
          response = await makeRequest(newToken)
          
          // If still unauthorized after refresh, redirect to login
          if (response.status === 401) {
            console.error('Still unauthorized after token refresh')
            this.clearTokens()
            if (typeof window !== 'undefined') {
              window.location.href = '/'
            }
            throw new Error('Session expired. Please login again.')
          }
        } catch (error) {
          // Refresh failed, user needs to login again
          console.error('Token refresh failed:', error)
          this.clearTokens()
          if (typeof window !== 'undefined') {
            window.location.href = '/'
          }
          throw new Error('Session expired. Please login again.')
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}`)
      }

      return response
    },
    async login(username, password) {
      // Use unified auth system
      const unifiedAuth = await import('../services/unifiedAuth.js')
      const result = await unifiedAuth.default.login({ username, password })

      if (!result.success) {
        throw new Error(`Login failed: ${result.errors.join(', ')}`)
      }

      // Set tokens based on what systems are available
      if (result.scheduler?.success) {
        // Use scheduler tokens (preferred for tenant system)
        const schedulerToken = result.scheduler.token
        // Note: We only have access token from scheduler, no separate refresh for this flow
        this.setTokens(schedulerToken, localStorage.getItem('scheduler_refresh_token') || schedulerToken)

        // Fetch user data from tenant API
        try {
          const userResponse = await this.makeAuthenticatedRequest(`${API_BASE_URL}/users/me/`)
          const userData = await userResponse.json()
          this.setUser(userData)
        } catch (error) {
          console.warn('Could not fetch user data from scheduler API:', error)
          // Set minimal user data
          this.setUser({ username, systems: result })
        }
      } else if (result.admin?.success) {
        // Fallback to admin token if scheduler not available
        const adminToken = result.admin.token
        this.setTokens(adminToken, adminToken)
        this.setUser({ username, systems: result, isAdminOnly: true })
      }

      // Store system capabilities for UI decisions
      this.systemCapabilities = {
        canSchedule: result.scheduler?.success || false,
        canManageCompanies: result.admin?.success || false,
        canViewInvoicing: result.admin?.success || false,
        availableSystems: {
          scheduler: result.scheduler?.success || false,
          admin: result.admin?.success || false
        }
      }
      localStorage.setItem('system_capabilities', JSON.stringify(this.systemCapabilities))

      return this.user
    }
  },
})